import {StrictMode} from 'react'
import {createRoot, hydrateRoot} from 'react-dom/client'
import posthog from 'posthog-js'
import {PostHogProvider} from 'posthog-js/react'
import App from './App.jsx'
import {getActiveSupabaseUrl} from './lib/supabase'
import './index.css'
import './styles/design-tokens.css'

// Environment + platform detection for PostHog. The Capacitor iOS shell loads
// the JS bundle from capacitor://localhost, so PostHog's default $current_url
// is meaningless in dashboards. Detect prod vs staging from the active
// Supabase URL (honours the staging-route override for the App Store-review
// allowlist), then rewrite capacitor:// URLs to the canonical https host below.
const SUPABASE_URL = getActiveSupabaseUrl() || ''
const APP_ENV = SUPABASE_URL.includes('maxhnevvufpviuuwcjpi') ? 'production'
              : SUPABASE_URL.includes('iwyqmtttfdiudvnuavtm') ? 'staging'
              : 'local'
// Detect native iOS/Android shell. `window.Capacitor` exists in the web
// build too (it's bundled with the plugin JS), so checking its existence
// would mislabel browser traffic as native. Use the runtime helpers, which
// return 'web' / false on the browser and 'ios' / true inside the Capacitor
// shell. The `capacitor://` protocol check is kept as a safety net for
// platforms that don't expose the helper.
const NATIVE_PLATFORM = typeof window !== 'undefined' && typeof window.Capacitor?.getPlatform === 'function'
  ? window.Capacitor.getPlatform()
  : null
const IS_CAPACITOR = typeof window !== 'undefined' &&
  (window.location.protocol === 'capacitor:' || (NATIVE_PLATFORM && NATIVE_PLATFORM !== 'web'))
const APP_PLATFORM = IS_CAPACITOR ? (NATIVE_PLATFORM || 'ios') : 'web'
const CANONICAL_HOST = APP_ENV === 'production' ? 'teesync.co.uk'
                     : APP_ENV === 'staging' ? 'staging.teesync.co.uk'
                     : null

// PostHog: single analytics destination for product analytics, session replay,
// error capture, feature flags, and surveys/heatmaps.
// Host: PostHog Cloud EU (better GDPR posture for UK/EU users).
// Profiles: identified_only - anonymous traffic doesn't create person profiles
// (saves MTU quota); profiles materialise on posthog.identify() in AuthContext.
const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY
if (posthogKey) {
  posthog.init(posthogKey, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
    person_profiles: 'identified_only',
    // SPA route changes don't trigger automatic pageviews - App.jsx fires
    // posthog.capture('$pageview') on every useLocation change instead.
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    enable_heatmaps: true,
    capture_unhandled_errors: true,
    session_recording: {
      // TODO: 100% sample rate is the v1 setting. If MTU/replay quota approaches
      // the cap, drop to 0.25 here. See plans/posthog-reimplementation.md §7.
      maskAllInputs: true,
      maskInputOptions: {
        password: true,
      },
    },
    before_send: (event) => {
      if (!event || !CANONICAL_HOST) return event
      const props = event.properties
      if (!props) return event

      // Rewrite a capacitor:// URL to its https equivalent on the canonical host.
      // Leaves $direct, external referrers, and parse failures untouched.
      const rewriteUrl = (value) => {
        if (typeof value !== 'string' || !value.startsWith('capacitor://')) return value
        try {
          const u = new URL(value)
          const path = u.pathname || '/'
          return `https://${CANONICAL_HOST}${path}${u.search}${u.hash}`
        } catch {
          return value
        }
      }

      // Replace bare "localhost" host strings with the canonical host. The
      // capacitor bundle is loaded from capacitor://localhost so the SDK
      // populates $host / $referring_domain etc. with literal "localhost".
      const rewriteHost = (value) =>
        value === 'localhost' ? CANONICAL_HOST : value

      const urlFields = ['$current_url', '$session_entry_url', '$referrer', '$session_entry_referrer']
      urlFields.forEach((key) => {
        if (props[key] != null) props[key] = rewriteUrl(props[key])
      })

      const hostFields = ['$host', '$session_entry_host', '$referring_domain', '$session_entry_referring_domain']
      hostFields.forEach((key) => {
        if (props[key] != null) props[key] = rewriteHost(props[key])
      })

      // Keep $pathname in sync with $current_url when we rewrote it.
      if (typeof props.$current_url === 'string') {
        try {
          props.$pathname = new URL(props.$current_url).pathname || '/'
        } catch {
          // leave as-is on parse failure
        }
      }

      return event
    },
    loaded: (ph) => {
      if (import.meta.env.DEV) {
        ph.debug(false)
      }
    },
  })
  // Register super-properties synchronously, before any capture fires.
  // Registering inside `loaded` is too late: the SDK fires queued events
  // (incl. early $pageview) before the callback runs, so those events ship
  // with no `app_platform` - verified in production where ~60% of 30d
  // pageviews arrived with a null app_platform.
  posthog.register({
    app_environment: APP_ENV,
    app_platform: APP_PLATFORM,
  })
} else if (import.meta.env.DEV) {
  console.warn('PostHog key missing - analytics, replay, and flags disabled.')
}

// react-snap prerenders each marketing route to static HTML at build time.
// When a visitor lands on a prerendered route, the #root element already has
// content from the snapshot - hydrate over the top instead of replacing it.
// For SPA navigation and non-prerendered routes, fall back to a fresh render.
const rootEl = document.getElementById('root')
const tree = (
  <StrictMode>
    <PostHogProvider client={posthog}>
      <App />
    </PostHogProvider>
  </StrictMode>
)

if (rootEl.hasChildNodes()) {
  hydrateRoot(rootEl, tree)
} else {
  createRoot(rootEl).render(tree)
}
